import React, { useCallback, useEffect, useState } from 'react';
import { Chip, Tabs, Tab, Select, SelectItem, addToast } from '@heroui/react';
import { Pencil } from 'lucide-react';
import DiscrepanciesApi from '../../../api/discrepanciesApi.js';
import {
	DISC_STATUS_CONFIG,
	DISC_PRIORITY,
	formatDateWithTime,
	normalizePerson,
	buildSaveDiscrepancyPayload,
	normalizeAssignToId
} from '../utils/discrepancyDisplay.js';

// ─── Discrepancies section ────────────────────────────────────────────────────
//
// Canonical, "dumb" discrepancies table shared by ResourcePanel (View Resource)
// and CheckInScreen. Receives data + callbacks + raw boolean perms via props —
// no internal Redux selectors — so each surface adapts its own perm-derivation
// externally. Uses a `div role="table"` grid (NOT HeroUI <Table>) so the inline
// <Select> popovers aren't swallowed by React Aria's table focus management and
// so the `react-heroui-layout.twig` <th>/<td> padding strip rule doesn't apply
// (see project memory `feedback_hero_ui_table_role_attr`).

const STATUS_OPTIONS = Object.entries(DISC_STATUS_CONFIG).map(([key, cfg]) => ({
	key,
	label: cfg.label
}));
const PRIORITY_OPTIONS = Object.entries(DISC_PRIORITY).map(([numKey, label]) => ({
	key: String(numKey),
	label
}));

function DiscrepanciesSection({
	discrepancies,
	userSettings,
	onDiscrepancyClick,
	canViewDiscrepancies,
	canChangeDiscrepancyStatus,
	canManageDiscrepancies,
	orgUnique,
	onDiscrepancyUpdated,
	mechanics
}) {
	const [filter, setFilter] = useState('all');
	const [items, setItems] = useState(Array.isArray(discrepancies) ? discrepancies : []);
	const [editingCell, setEditingCell] = useState(null); // { id: string, field: string }
	const [saving, setSaving] = useState(false);

	// Sync from parent when the prop is refreshed (e.g. after parent re-fetches)
	useEffect(() => {
		setItems(Array.isArray(discrepancies) ? discrepancies : []);
	}, [discrepancies]);

	const saveEdit = useCallback(
		async (discrepancyId, field, value) => {
			const d = items.find(x => String(x.id) === String(discrepancyId));
			if (!d) return;
			const prevValue = d[field];
			// Optimistic update
			setItems(cur =>
				cur.map(x => (String(x.id) === String(discrepancyId) ? { ...x, [field]: value } : x))
			);
			setEditingCell(null);
			setSaving(true);
			try {
				if (field === 'status') {
					await DiscrepanciesApi.changeStatus(discrepancyId, { status: value }, orgUnique);
				} else {
					const payload = buildSaveDiscrepancyPayload(d, { [field]: value });
					await DiscrepanciesApi.updateDiscrepancy(discrepancyId, payload, orgUnique);
				}
				addToast({ title: 'Saved', color: 'success' });
				onDiscrepancyUpdated?.();
			} catch (err) {
				// Revert optimistic update on failure
				setItems(cur =>
					cur.map(x =>
						String(x.id) === String(discrepancyId) ? { ...x, [field]: prevValue } : x
					)
				);
				const msg = err?.response?.data?.error?.[0] || err?.message || 'Save failed';
				addToast({ title: 'Save failed', description: msg, color: 'danger' });
			} finally {
				setSaving(false);
			}
		},
		[items, orgUnique, onDiscrepancyUpdated]
	);

	const filtered = filter === 'all' ? items : items.filter(d => d.status === filter);
	const isClickable = canViewDiscrepancies && typeof onDiscrepancyClick === 'function';

	return (
		<div>
			{/* P: heroui-discr-filter scopes the primary-bg CSS override in react-heroui-layout.twig */}
			<div className="heroui-discr-filter mb-3">
				<Tabs
					aria-label="Discrepancies status filter"
					selectedKey={filter}
					onSelectionChange={key => setFilter(String(key))}
					size="sm"
					variant="solid"
					color="primary"
				>
					<Tab key="all" title="All" />
					<Tab key="new" title="New" />
					<Tab key="open" title="Open" />
					<Tab key="inprogress" title="In Progress" />
					<Tab key="deferred" title="Deferred" />
					<Tab key="closed" title="Closed" />
				</Tabs>
			</div>

			{!filtered.length ? (
				<p className="text-xs text-default-400 italic">No discrepancies found.</p>
			) : (
				// Div-grid layout (not HeroUI <Table>) so the inline <Select> popovers
				// aren't swallowed by React Aria's table focus management. Matches the
				// working pattern in DiscrepancyView.js.
				<div role="table" aria-label="Discrepancies" className="w-full text-sm">
					<div
						role="row"
						className="grid grid-cols-[minmax(0,2fr)_120px_minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,1fr)] gap-2 px-2 py-2 text-xs font-semibold text-default-500 uppercase tracking-wide border-b border-default-200"
					>
						<div role="columnheader">Name</div>
						<div role="columnheader" className="text-center">
							Status
						</div>
						<div role="columnheader">Priority</div>
						<div role="columnheader">Assigned To</div>
						<div role="columnheader" className="text-right">
							Date
						</div>
					</div>
					{filtered.map((d, idx) => {
						const statusCfg = DISC_STATUS_CONFIG[d.status] || {
							label: d.status || '—',
							color: 'default'
						};
						const priority = DISC_PRIORITY[d.priority] || String(d.priority || '—');
						const date = d.date || d.createdAt || d.created_at || null;
						// `normalizePerson(rawId)` historically returned `{fullName: "14160"}`
						// for an unresolved-ID assignTo, leaking the raw ID into the UI
						// during the ~2s while the discrepancy detail GET is in flight.
						// Only trust normalizePerson when assignTo is already an object;
						// otherwise look up the cached mechanic by id, then fall through
						// to the legacy flat name fields, then "—".
						let resolvedName = null;
						if (d.assignTo && typeof d.assignTo === 'object') {
							resolvedName = normalizePerson(d.assignTo)?.fullName || null;
						} else if (d.assignTo != null && d.assignTo !== '' && Array.isArray(mechanics)) {
							const mech = mechanics.find(m => String(m.id) === String(d.assignTo));
							if (mech) {
								resolvedName =
									[mech.name, mech.surname].filter(Boolean).join(' ').trim() || null;
							}
						}
						const assignedTo =
							resolvedName ||
							(d.assignedName
								? `${d.assignedName} ${d.assignedSurname || ''}`.trim()
								: '—');
						const authorName =
							normalizePerson(d.author)?.fullName ||
							[d.authorName, d.authorSurname].filter(Boolean).join(' ') ||
							'—';
						const isEditingStatus =
							editingCell?.id === String(d.id) && editingCell.field === 'status';
						const isEditingPriority =
							editingCell?.id === String(d.id) && editingCell.field === 'priority';
						const isEditingAssign =
							editingCell?.id === String(d.id) && editingCell.field === 'assignTo';

						return (
							<div
								role="row"
								key={String(d.id)}
								className={`grid grid-cols-[minmax(0,2fr)_120px_minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,1fr)] gap-2 px-2 py-2 items-center ${
									idx % 2 === 1 ? 'bg-default-50' : ''
								} hover:bg-default-100`}
							>
								{/* Name — click navigates to detail screen */}
								<div role="cell">
									{isClickable ? (
										<button
											onClick={() => onDiscrepancyClick(d)}
											className="font-semibold hover:underline text-left"
											style={{ color: 'hsl(var(--heroui-primary))' }}
										>
											{d.name || '—'}
										</button>
									) : (
										<div className="font-semibold">{d.name || '—'}</div>
									)}
									{d.description ? (
										<div className="text-default-600 text-xs">{d.description}</div>
									) : null}
									<div className="text-default-400 text-xs">By: {authorName}</div>
								</div>

								{/* Status — inline select when canChangeDiscrepancyStatus */}
								<div role="cell" className="text-center">
									{canChangeDiscrepancyStatus && isEditingStatus ? (
										<Select
											size="sm"
											className="min-w-[120px]"
											defaultOpen
											autoFocus
											aria-label="Edit discrepancy status"
											selectedKeys={new Set([d.status || 'new'])}
											onSelectionChange={keys => {
												const val = Array.from(keys)[0];
												if (val) saveEdit(d.id, 'status', val);
											}}
											isDisabled={saving}
										>
											{STATUS_OPTIONS.map(opt => (
												<SelectItem key={opt.key}>{opt.label}</SelectItem>
											))}
										</Select>
									) : (
										<div
											onClick={
												canChangeDiscrepancyStatus
													? e => {
															e.stopPropagation();
															setEditingCell({
																id: String(d.id),
																field: 'status'
															});
														}
													: undefined
											}
											className={`inline-flex${canChangeDiscrepancyStatus ? ' cursor-pointer' : ''}`}
											title={
												canChangeDiscrepancyStatus ? 'Click to edit' : undefined
											}
										>
											<Chip size="sm" variant="flat" color={statusCfg.color}>
												{statusCfg.label}
											</Chip>
										</div>
									)}
								</div>

								{/* Priority — inline select when canManageDiscrepancies */}
								<div role="cell">
									{canManageDiscrepancies && isEditingPriority ? (
										<Select
											size="sm"
											className="min-w-[100px]"
											defaultOpen
											autoFocus
											aria-label="Edit discrepancy priority"
											selectedKeys={new Set([String(d.priority || '')])}
											onSelectionChange={keys => {
												const val = Array.from(keys)[0];
												if (val) saveEdit(d.id, 'priority', val);
											}}
											isDisabled={saving}
										>
											{PRIORITY_OPTIONS.map(opt => (
												<SelectItem key={opt.key}>{opt.label}</SelectItem>
											))}
										</Select>
									) : (
										<div
											onClick={
												canManageDiscrepancies
													? e => {
															e.stopPropagation();
															setEditingCell({
																id: String(d.id),
																field: 'priority'
															});
														}
													: undefined
											}
											className={
												canManageDiscrepancies
													? 'group inline-flex items-center gap-1 px-2 py-1 -mx-2 -my-1 rounded cursor-pointer hover:bg-default-100 transition-colors'
													: ''
											}
											title={canManageDiscrepancies ? 'Click to edit' : undefined}
										>
											<span>{priority}</span>
											{canManageDiscrepancies ? (
												<Pencil className="w-3 h-3 text-default-400 opacity-0 group-hover:opacity-60 transition-opacity" />
											) : null}
										</div>
									)}
								</div>

								{/* Assigned To — inline mechanic select when canManageDiscrepancies */}
								<div role="cell">
									{canManageDiscrepancies && isEditingAssign ? (
										<Select
											size="sm"
											className="min-w-[140px]"
											defaultOpen
											autoFocus
											aria-label="Edit discrepancy assigned mechanic"
											selectedKeys={
												new Set([String(normalizeAssignToId(d.assignTo) ?? '')])
											}
											onSelectionChange={keys => {
												const val = Array.from(keys)[0];
												saveEdit(d.id, 'assignTo', val === '' ? null : val);
											}}
											isDisabled={saving}
										>
											<SelectItem key="">— Unassigned —</SelectItem>
											{mechanics.map(m => (
												<SelectItem key={String(m.id)}>
													{[m.name, m.surname].filter(Boolean).join(' ') ||
														`User #${m.id}`}
												</SelectItem>
											))}
										</Select>
									) : (
										<div
											onClick={
												canManageDiscrepancies
													? e => {
															e.stopPropagation();
															setEditingCell({
																id: String(d.id),
																field: 'assignTo'
															});
														}
													: undefined
											}
											className={
												canManageDiscrepancies
													? 'group inline-flex items-center gap-1 px-2 py-1 -mx-2 -my-1 rounded cursor-pointer hover:bg-default-100 transition-colors'
													: ''
											}
											title={canManageDiscrepancies ? 'Click to edit' : undefined}
										>
											<span>{assignedTo}</span>
											{canManageDiscrepancies ? (
												<Pencil className="w-3 h-3 text-default-400 opacity-0 group-hover:opacity-60 transition-opacity" />
											) : null}
										</div>
									)}
								</div>

								<div role="cell" className="text-xs text-default-500 text-right">
									{date ? formatDateWithTime(date, userSettings) : '—'}
								</div>
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
}

export default DiscrepanciesSection;
